/**
 * The LaTeX view's Blocks tab (DESIGN-14, Ḥassān, 17 Oct): every block of the document as a card,
 * what it renders as first and its LaTeX underneath, in the document's order. Reading-first: no
 * gutters, no cursor. Selecting a block in the document outlines its card; clicking a card's
 * rendered part selects the block; the line range opens the Code tab there.
 *
 * On a phone (his phone round, 18 Oct) the cards are read-only and denser: reading-size rendering,
 * Copy as an icon, no "Selected" tag (the outline says it), code over 6 lines folded to 4, tables
 * over 4 rows folded, wide content scrolling inside its card under a fade, and a paragraph with no
 * LaTeX commands saying so instead of repeating itself.
 */
import i18n from "i18next";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { IconCopy, IconCheck, IconPencil } from "@tabler/icons-react";
import { BlockRenderer, type RenderableBlock } from "@/components/preview/BlockRenderer";
import { useDocumentCheck } from "@/components/mobile/useDocumentCheck";
import { useApiContext } from "@/contexts/ApiContext";
import type { CheckIssue } from "@/lib/documentCheck";
import type { LineRange } from "./block-line-map";
import {
  FOLD_OVER, FOLD_TO, PHONE_FOLD_OVER, PHONE_FOLD_TO, PHONE_TABLE_ROWS,
  foldsCode, isPlainText, referenceFacts, type BlockSlice,
} from "./latexView";
import { testIds } from "@/testIds";
import { cn } from "@/lib/cn";
import styles from "./LatexViewTabs.module.scss";

const T = testIds.latexView;

export interface LatexBlocksTabProps {
  documentId: string;
  slices: BlockSlice[];
  blocks: (RenderableBlock & { id: string })[];
  focusedBlockId?: string | null;
  /** Selects the block in the document (Edit). Without it (the phone, read-only) cards have no Edit. */
  onScrollToBlock?: (blockId: string) => void;
  /** Opens the Code tab with these lines tinted; `slice` says which card asked. */
  onOpenLines: (range: LineRange, slice: BlockSlice) => void;
  /** The phone's denser, read-only cards. */
  variant?: "desktop" | "phone";
  /** A card to bring into view on opening (the phone's "Back to block"). */
  revealBlockId?: string | null;
}

/** "\subsection{…}" → commands in ink weight, the rest slate (Ḥassān's code strip). */
function CodeText({ code }: { code: string }) {
  const parts = code.split(/(\\[A-Za-z@]+\*?)/g);
  return <>{parts.map((p, i) => (p.startsWith("\\") ? <b key={i}>{p}</b> : <span key={i}>{p}</span>))}</>;
}

export function typeLabel(type: string): string {
  const k = `editor:latexView.blocks.type.${type}`;
  return i18n.exists(k) ? i18n.t(k) : type.charAt(0).toUpperCase() + type.slice(1);
}

function tableSize(block?: RenderableBlock): { rows: number; columns: number } | null {
  if (block?.type !== "table") return null;
  const rows = Array.isArray(block.content.rows) ? (block.content.rows as unknown[][]) : [];
  const headers = Array.isArray(block.content.headers) ? (block.content.headers as unknown[]) : [];
  return { rows: rows.length, columns: Math.max(headers.length, ...rows.map((r) => (Array.isArray(r) ? r.length : 0)), 0) };
}

function Card({ slice, block, selected, reveal, issues, phone, onScrollToBlock, onOpenLines }: {
  slice: BlockSlice; block?: RenderableBlock & { id: string }; selected: boolean; reveal: boolean; issues: CheckIssue[];
  phone: boolean; onScrollToBlock?: (id: string) => void; onOpenLines: (r: LineRange, s: BlockSlice) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const renderedRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const type = block?.type ?? "block";
  const lines = slice.code.split("\n");
  const foldOver = phone ? PHONE_FOLD_OVER : FOLD_OVER;
  const foldTo = phone ? PHONE_FOLD_TO : FOLD_TO;
  const canFold = foldsCode(type, slice.code, foldOver);
  const folds = canFold && !open;
  const shown = folds ? lines.slice(0, foldTo).join("\n") : slice.code;
  const facts = type === "paragraph" ? referenceFacts(slice.code) : null;
  const plain = phone && type === "paragraph" && isPlainText(slice.code);
  const table = phone ? tableSize(block) : null;
  const tableFolds = !!table && table.rows > PHONE_TABLE_ROWS;
  const error = issues.find((x) => x.severity === "error");
  const issue = error ?? issues[0];

  useEffect(() => {
    if (selected || reveal) ref.current?.scrollIntoView?.({ block: reveal ? "start" : "nearest", behavior: reveal ? "auto" : "smooth" });
  }, [selected, reveal]);

  // A wide table or equation scrolls inside its card; a fade on the right says there is more.
  useLayoutEffect(() => {
    const el = renderedRef.current;
    if (!phone || !el) return;
    setOverflows(el.scrollWidth > el.clientWidth + 2);
  }, [phone, block, tableOpen]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(slice.code); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* no clipboard */ }
  };

  return (
    <div
      ref={ref}
      data-testid={T.blockCard}
      data-block-id={slice.blockId}
      data-block-type={type}
      data-selected={selected || undefined}
      aria-current={selected || undefined}
      data-issue={issue?.severity}
      className={cn(styles.card, selected && styles.cardSelected)}
    >
      <div className={styles.cardHead}>
        <span className={styles.cardType}>{typeLabel(type)}</span>
        <button type="button" data-testid={T.blockLines} className={styles.cardWhere} onClick={() => onOpenLines(slice.range, slice)}>
          {slice.range.start === slice.range.end
            ? i18n.t("editor:latexView.blocks.line", { n: slice.range.start })
            : i18n.t("editor:latexView.blocks.lines", { start: slice.range.start, end: slice.range.end })}
        </button>
        <span className={styles.cardActions}>
          {selected && !phone && <span className={styles.selectedTag}>{i18n.t("editor:latexView.blocks.selected")}</span>}
          {onScrollToBlock && (
            <button type="button" data-testid={T.blockEdit} className={styles.cardAction} onClick={() => onScrollToBlock(slice.blockId)}>
              <IconPencil size={13} /> {error ? i18n.t("editor:latexView.blocks.fix") : i18n.t("editor:latexView.blocks.edit")}
            </button>
          )}
          {phone ? (
            <button
              type="button"
              data-testid={T.blockCopy}
              className={styles.copyIcon}
              aria-label={copied ? i18n.t("editor:latexView.blocks.copied") : i18n.t("editor:latexView.blocks.copyLatex")}
              onClick={copy}
            >
              {copied ? <IconCheck size={17} /> : <IconCopy size={17} />}
            </button>
          ) : (
            <button type="button" data-testid={T.blockCopy} className={styles.cardAction} onClick={copy}>
              {copied ? <IconCheck size={13} /> : <IconCopy size={13} />} {copied ? i18n.t("editor:latexView.blocks.copied") : i18n.t("editor:latexView.blocks.copy")}
            </button>
          )}
        </span>
      </div>

      {block && (
        <div
          ref={renderedRef}
          className={cn(
            styles.rendered,
            onScrollToBlock && styles.renderedLink,
            overflows && styles.renderedFade,
            tableFolds && !tableOpen && styles.tableFolded,
          )}
          data-testid={T.blockRendered}
          {...(onScrollToBlock && {
            role: "button",
            tabIndex: 0,
            onClick: () => onScrollToBlock(slice.blockId),
            onKeyDown: (e: KeyboardEvent) => { if (e.key === "Enter") onScrollToBlock(slice.blockId); },
          })}
        >
          {error ? <p className={styles.notRendered}>{i18n.t("editor:latexView.blocks.notRendered")}</p> : <BlockRenderer block={block} />}
        </div>
      )}
      {tableFolds && table && (
        <button type="button" data-testid={T.blockTableFold} className={styles.foldRow} onClick={() => setTableOpen((o) => !o)}>
          <span>{tableOpen ? i18n.t("editor:latexView.blocks.showLess") : i18n.t("editor:latexView.blocks.showRows", { count: table.rows })}</span>
          {table.columns > 3 && <span className={styles.foldHint}>{i18n.t("editor:latexView.blocks.columnsScroll", { count: table.columns })}</span>}
        </button>
      )}

      {issue && (
        <div data-testid={T.blockIssue} data-severity={issue.severity} className={cn(styles.issue, issue.severity === "error" ? styles.issueError : styles.issueWarning)}>
          <b>{issue.severity === "error" ? i18n.t("editor:latexView.blocks.error") : i18n.t("editor:latexView.blocks.warning")}</b> {issue.message}
        </div>
      )}

      {plain ? (
        <p data-testid={T.blockPlain} className={styles.plainLine}>{i18n.t("editor:latexView.blocks.plainText")}</p>
      ) : (
        <pre className={styles.code}><CodeText code={shown} /></pre>
      )}
      {canFold && !plain && (
        <button type="button" data-testid={T.blockFold} className={phone ? styles.foldRow : styles.fold} onClick={() => setOpen((o) => !o)}>
          {open
            ? i18n.t("editor:latexView.blocks.showLess")
            : phone
              ? i18n.t("editor:latexView.blocks.showMore", { count: lines.length - foldTo })
              : i18n.t("editor:latexView.blocks.showAll", { count: lines.length, hidden: lines.length - foldTo })}
        </button>
      )}

      {facts && (facts.citations > 0 || facts.references > 0) && (
        <div className={styles.facts}>
          {[
            facts.citations > 0 ? i18n.t("editor:latexView.blocks.citations", { count: facts.citations }) : null,
            facts.references > 0 ? i18n.t("editor:latexView.blocks.references", { count: facts.references }) : null,
          ].filter(Boolean).join(" · ")}
        </div>
      )}
    </div>
  );
}

export function LatexBlocksTab({ documentId, slices, blocks, focusedBlockId, onScrollToBlock, onOpenLines, variant = "desktop", revealBlockId = null }: LatexBlocksTabProps) {
  const { fetchWithAuth } = useApiContext();
  // The same check the phone runs, here only while the Blocks tab is open; it waits for edits to settle.
  const check = useDocumentCheck(documentId, blocks as never, fetchWithAuth);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const byId = useMemo(() => new Map(blocks.map((b) => [b.id, b])), [blocks]);
  const issuesById = useMemo(() => {
    const m = new Map<string, CheckIssue[]>();
    for (const i of check.issues ?? []) if (i.blockId) m.set(i.blockId, [...(m.get(i.blockId) ?? []), i]);
    return m;
  }, [check.issues]);
  const issueCount = [...issuesById.values()].reduce((n, l) => n + l.length, 0);
  const shown = onlyIssues ? slices.filter((s) => issuesById.has(s.blockId)) : slices;

  return (
    <div data-testid={T.blocks} className={styles.scroll}>
      <div className={styles.summary}>
        <span>{i18n.t("editor:latexView.blocks.count", { count: slices.length })}</span>
        {issueCount > 0 && (
          <button
            type="button"
            data-testid={T.issuesChip}
            aria-pressed={onlyIssues}
            className={cn(styles.issuesChip, onlyIssues && styles.issuesChipOn)}
            onClick={() => setOnlyIssues((v) => !v)}
          >
            {i18n.t("editor:latexView.blocks.issues", { count: issueCount })}
          </button>
        )}
        {check.running && <span className={styles.checking}>{i18n.t("editor:latexView.blocks.checking")}</span>}
      </div>
      {shown.map((s) => (
        <Card
          key={s.blockId}
          slice={s}
          block={byId.get(s.blockId)}
          selected={s.blockId === focusedBlockId}
          reveal={s.blockId === revealBlockId}
          issues={issuesById.get(s.blockId) ?? []}
          phone={variant === "phone"}
          onScrollToBlock={onScrollToBlock}
          onOpenLines={onOpenLines}
        />
      ))}
    </div>
  );
}
